import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getEconomicSupplierList,
  updateEconomicSupplier,
  deleteEconomicSupplier,
} from "../../../../../../apis/Business/EconomicSupplier";
import {
  getBusinessList,
  getVehicleSubcategory,
} from "../../../../../../apis/Business/Business";
import { getVehicleFuelSubcategory } from "../../../../../../apis/Business/FuelVehicleApi";
import { getOtherVehicleSubcategory } from "../../../../../../apis/Business/Business";
import { getIngoingTransportSubcategory } from "../../../../../../apis/Business/IngoingTransportApi";
import { getOutgoingTransportSubcategory } from "../../../../../../apis/Business/OutgoingTransportApi";
import { getCustomSubcategory } from "../../../../../../apis/Business/InventoryApi";
import { getTravelSubcategory } from "../../../../../../apis/Business/TravelApi";
import { getWasteSubcategory } from "../../../../../../apis/Business/WasteApi";
import { YearTabContext } from "../../../../../../components/Skeleton";
import { useContext } from "react";
import getDropdownOptions from "../../../../../Company/EPR/components/SupplierTable/getDropdownOptions";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const EPRTable = ({ businessId, mainBusinessId, category }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownFacility, setDropdownFacility] = useState([]);
  const yearTab = useContext(YearTabContext);
  const [subcategoryVehicle, setSubcategoryVehicle] = useState([]);
  const [subcategoryFuelVehicle, setSubcategoryFuelVehicle] = useState([]);
  const [subcategoryOtherVehicle, setSubcategoryOtherVehicle] = useState([]);
  const [subcategoryIngoingTransport, setSubcategoryIngoingTransport] =
    useState([]);
  const [subcategoryOutgoingTransport, setSubcategoryOutgoingTransport] =
    useState([]);
  const [subcategoryCustomInventory, setSubcategoryCustomInventory] = useState(
    []
  );
  const [subcategoryTravel, setSubcategoryTravel] = useState([]);
  const [subcategoryWaste, setSubcategoryWaste] = useState([]);
  const [categoriesWithSubcategories, setCategoriesWithSubcategories] =
    useState({
      Energy: [t("MAINS_INPUT"), t("HOME_INPUT"), t("SYMBIOTIC_INPUTS")],
      Water: [
        t("WATER_USAGE"),
        t("WATER_FOUND_LOCATION"),
        t("EQUIPMENT_LOCATION"),
        t("WATER_SYMBIOTIC_INPUT"),
      ],
      "Registered Vehicle": [],
      "Fuel Vehicle": [],
      "Other Vehicle": [],
      "Emission from gasses": [
        t("REFRIGERATION_INPUTS"),
        t("INDUSTRIAL_GAS_INPUTS"),
        t("SUPPRESSANT_INPUTS"),
      ],
      "Ingoing Transport": [],
      "Outgoing Transport": [],
      Inventory: [
        t("INV_VEHICLE"),
        t("MACHINERY"),
        t("INV_BUILDINGS"),
        t("PURCHASED_SERVICES"),
        t("INVENTORY_INGOING_PACKAGING"),
        t("INVENTORY_PURCHASED_PACKAGING"),
        t("CHEMICALS"),
      ],
      "Business Travel and Hotel Stays": [],
      "Employee Commute": [],
      Remote: [],
      "Surplus Resources": [
        t("TREATMENT_INGOING_PACKAGING"),
        t("SURPLUS_ENERGY"),
        t("WATER"),
      ],
    });

  const [categoriesData, setCategoriesData] = useState(
    categoriesWithSubcategories
  );

  const [dropdownCategory, setDropdownCategory] = useState([]);

  const [dropdownSubcategory, setDropdownSubcategory] = useState([]);

  const [dropdownEmission, setDropdownEmission] = useState([]);

  const [facilityId, setFacilityId] = useState();

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getEconomicSupplierList({
      businessId: mainBusinessId,
      setObjects: setData,
      facilityId: businessId,
      category: category,
    });
    getBusinessList({
      setFacilities: setDropdownFacility,
      filterYear: yearTab,
    });
  }, [businessId, yearTab, mainBusinessId, category]);

  const dynamicPlaceholders = useMemo(
    () => ({
      "category.value": {
        targetField: ["subcategory.value", "emission_factor.value"],
        getPlaceholder: (selectedValue, dropdownData) => {
          const subcategories = categoriesData[selectedValue].map(
            (subcategory, index) => ({
              id: index + 1,
              title: subcategory,
              name: subcategory,
              value: subcategory,
            })
          );
          if (subcategories) {
            setDropdownSubcategory(subcategories);
          }

          const fetchOptions = async () => {
            const options = await getDropdownOptions(
              selectedValue,
              null,
              businessId,
              t
            );
            setDropdownEmission(options);
          };
          return fetchOptions();
        },
        dropdownDataKey: "category.value",
      },
      "facility.value": {
        getPlaceholder: (selectedValue, dropdownData) => {
          const foundItem = dropdownData.find(
            (item) =>
              item && typeof item === "object" && item.name === selectedValue
          );

          if (foundItem) {
            setFacilityId(foundItem.id);
          }
          if (foundItem && foundItem.id) {
            getVehicleSubcategory({
              setSubcategoryNames: setSubcategoryVehicle,
              businessId: foundItem.id,
            });
            getVehicleFuelSubcategory({
              setSubcategoryNames: setSubcategoryFuelVehicle,
              businessId: foundItem.id,
            });
            getOtherVehicleSubcategory({
              setSubcategoryNames: setSubcategoryOtherVehicle,
              businessId: foundItem.id,
            });
            getIngoingTransportSubcategory({
              businessId: foundItem.id,
              setSubcategoryNames: setSubcategoryIngoingTransport,
            });
            getOutgoingTransportSubcategory({
              businessId: foundItem.id,
              setSubcategoryNames: setSubcategoryOutgoingTransport,
            });
            getCustomSubcategory({
              businessId: foundItem.id,
              setSubcategoryNames: setSubcategoryCustomInventory,
            });
            getTravelSubcategory({
              businessId: foundItem.id,
              setSubcategoryNames: setSubcategoryTravel,
            });
            getWasteSubcategory({
              businessId: foundItem.id,
              setSubcategoryNames: setSubcategoryWaste,
            });
          }
        },
        dropdownDataKey: "facility.value",
      },
      "subcategory.value": {
        targetField: "emission_factor.value",
        getPlaceholder: (selectedValue, dropdownData) => {
          const fetchOptions = async () => {
            const options = await getDropdownOptions(
              null,
              selectedValue,
              businessId,
              t
            );
            setDropdownEmission(options);
          };
          fetchOptions();
        },
        dropdownDataKey: "subcategory.value",
      },
    }),
    [categoriesData, businessId, t]
  );

  useEffect(() => {
    fetchData();
    const categories = Object.keys(categoriesData).map((category, index) => ({
      id: index + 1,
      name: category,
      value: category,
    }));
    setDropdownCategory(categories);
  }, [fetchData, categoriesData]);

  const handleEdit = useCallback(
    (editedItem) => {
      const generateSubcategoryKey = (category, subcategory) => {
        const categoryPrefix = category.toLowerCase().replace(/\s+/g, "_");
        const subcategorySuffix = subcategory
          .toLowerCase()
          .replace(/\s+/g, "_");
        return `${categoryPrefix}_${subcategorySuffix}`;
      };

      let typeObj = {};
      if (editedItem["emission_factor.value"]) {
        typeObj = dropdownEmission.find(
          (item) => item.value === editedItem["emission_factor.value"].value
        );
      } else if (editedItem["emission_factor"].value) {
        typeObj = dropdownEmission.find(
          (item) => item.value === editedItem["emission_factor"].value
        );
      }

      const typedb =
        typeObj && typeObj.typeDB
          ? typeObj.typeDB
          : editedItem.type && typeof editedItem.type === "string"
          ? editedItem.type
          : data["typedb"];

      updateEconomicSupplier({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        facility: editedItem["facility.value"]
          ? editedItem["facility.value"].value
          : editedItem["facility"]
          ? editedItem["facility"].value
          : null,
        category: editedItem["category.value"]
          ? editedItem["category.value"].value
          : editedItem["category"]
          ? editedItem["category"].value
          : null,
        subcategory: editedItem["subcategory.value"]
          ? editedItem["subcategory.value"].value
          : editedItem["subcategory"]
          ? editedItem["subcategory"].value
          : null,
        facility_id: facilityId,
        subcategory_key: generateSubcategoryKey(
          editedItem["category.value"]
            ? editedItem["category.value"].value
            : editedItem["category"]
            ? editedItem["category"].value
            : null,
          editedItem["subcategory.value"]
            ? editedItem["subcategory.value"].value
            : editedItem["subcategory"]
            ? editedItem["subcategory"].value
            : null
        ),
        emission_factor: editedItem["emission_factor.value"]
          ? editedItem["emission_factor.value"].value
          : editedItem["emission_factor"]
          ? editedItem["emission_factor"].value
          : null,
        is_manuel:
          editedItem["is_manuel"] === "Yes" || editedItem["is_manuel"] === "Ja",
        typedb: typedb,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, data, setData, facilityId, dropdownEmission]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteEconomicSupplier({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  useEffect(() => {
    if (subcategoryVehicle.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        "Registered Vehicle": subcategoryVehicle,
      }));
    }
    if (subcategoryFuelVehicle.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        "Fuel Vehicle": subcategoryFuelVehicle,
      }));
    }
    if (subcategoryOtherVehicle.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        "Other Vehicle": subcategoryOtherVehicle,
      }));
    }
    if (subcategoryIngoingTransport.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        "Ingoing Transport": subcategoryIngoingTransport,
      }));
    }
    if (subcategoryOutgoingTransport.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        "Outgoing Transport": subcategoryOutgoingTransport,
      }));
    }
    if (subcategoryCustomInventory.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        Inventory: [...(prev.Inventory || []), ...subcategoryCustomInventory],
      }));
    }
    if (subcategoryTravel.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        "Business Travel and Hotel Stays": subcategoryTravel,
      }));
    }
    if (subcategoryWaste.length > 0) {
      setCategoriesWithSubcategories((prev) => ({
        ...prev,
        "Surplus Resources": [
          ...(prev["Surplus Resources"] || []),
          ...subcategoryWaste,
        ],
      }));
    }
  }, [
    subcategoryVehicle,
    subcategoryFuelVehicle,
    subcategoryOtherVehicle,
    subcategoryIngoingTransport,
    subcategoryOutgoingTransport,
    subcategoryCustomInventory,
    subcategoryTravel,
    subcategoryWaste,
  ]);

  useEffect(() => {
    setCategoriesData(categoriesWithSubcategories);
  }, [categoriesWithSubcategories]);

  const columns = useMemo(
    () => [
      {
        accessor: "invoice_date",
        header: t("INVOICE_DATE"),
        type: "text",
        canSort: true,
        placeholder: t("DD_MM_YYYY"),
        width: "120px",
      },
      {
        accessor: "invoice_number",
        header: t("INVOICE_NO"),
        type: "text",
        canSort: true,
        width: "90px",
      },
      {
        accessor: "text",
        header: t("NICKNAME"),
        type: "text",
        canSort: true,
        width: "90px",
      },

      {
        accessor: "manuel",
        header: t("MANUEL"),
        type: "number",
        canHide: true,
        placeholder: "kgCO₂e/per",
        width: "90px",
        optional: true,
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "amount",
        header: t("PRICE_INVOICE"),
        type: "number",
        canSort: true,
        canHide: true,
        placeholder: "0",
        optional: true,
        subtotalUnit: "DKK",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("PRICE_INVOICE_TIP"),
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "80px",
      },
    ],
    [t]
  );

  const disableRules = {
    supplier: () => true,
    carbon: () => true,
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "facility.value": dropdownFacility,
      "category.value": dropdownCategory,
      "subcategory.value": dropdownSubcategory,
      "emission_factor.value": dropdownEmission,
    }),
    [dropdownFacility, dropdownCategory, dropdownSubcategory, dropdownEmission]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onEdit={handleEdit}
      onDelete={handleDelete}
      customInputs={{ carbon: CustomInputComponent }}
      dropdownData={memoizedDropdownData}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      noAddNewRow
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default EPRTable;
